import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import { AiLanguageVoice } from "../entities/ai-language-voice.entity";
import { useGetAILanguageVoicesQuery, useCountAILanguageVoicesQuery } from "../ai-language-voices.service";
import AILanguageVoicesTable from "../components/ai-language-voices-table";
import { FaGlobe, FaMicrophone, FaLink } from "react-icons/fa";

const StatCard = ({ title, count, icon, colorClass, borderClass, bgClass }: any) => (
  <div className={`p-4 rounded-xl border ${borderClass} ${bgClass} shadow-sm flex items-center justify-between`}>
    <div>
      <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-gray-800">{count ?? "-"}</h3>
    </div>
    <div className={`p-3 rounded-full ${colorClass} bg-white bg-opacity-60`}>
      {icon}
    </div>
  </div>
);

const AILanguageVoicesPage = () => {
  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError } =
    usePagination<AiLanguageVoice & { id: string }>({
      useGetDataQuery: useGetAILanguageVoicesQuery,
      useCountData: useCountAILanguageVoicesQuery,
    });

  // Fetch stats separately
  const { data: totalCount } = useCountAILanguageVoicesQuery({ where: {} });
  const { data: predefinedCount } = useCountAILanguageVoicesQuery({ where: { voiceSource: "PREDEFINED" } });
  const { data: inheritedCount } = useCountAILanguageVoicesQuery({ where: { voiceSource: "INHERITED" } });

  return (
    <div className="page_container space-y-6">
      <PageHeader text="AI Supported Languages" />

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="All Languages"
          count={totalCount}
          icon={<FaGlobe className="text-xl text-blue-600" />}
          colorClass="text-blue-600"
          borderClass="border-blue-100"
          bgClass="bg-gradient-to-br from-blue-50 to-indigo-50"
        />
        <StatCard
          title="Predefined Voices"
          count={predefinedCount}
          icon={<FaMicrophone className="text-xl text-emerald-600" />}
          colorClass="text-emerald-600"
          borderClass="border-emerald-100"
          bgClass="bg-gradient-to-br from-emerald-50 to-teal-50"
        />
        <StatCard
          title="Inherited Voices"
          count={inheritedCount}
          icon={<FaLink className="text-xl text-amber-600" />}
          colorClass="text-amber-600"
          borderClass="border-amber-100"
          bgClass="bg-gradient-to-br from-amber-50 to-orange-50"
        />
      </div>

      {isSuccess ? (
        <>
          <AILanguageVoicesTable data={data} />
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} />
        </>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading AI Language Voices</h3>
          <p className="text-red-600">There was an error fetching the AI language voices data. Please check the console for more details.</p>
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default AILanguageVoicesPage;
